import { GRID_HEIGHT, GRID_WIDTH } from './worldmap-renderer';

/**
 * The shipped world-map background asset and the exact source-pixel rows
 * (measured directly off that file's center column via ImageMagick, not
 * derived from the original source — resizing/re-encoding can shift edges
 * by a few px, so these are measured on the asset actually served) at which
 * the polar ice-cap band ends and the orange equatorial band begins/ends.
 *
 * Re-measured 2026-09-12 for a full re-export (different art entirely, same
 * equirectangular-with-polar-caps convention) — source was 6336x2688,
 * resized to 2048-wide before measuring (per the note above, never measure
 * off the pre-resize original). Both edges are a ~3px blend rather than one
 * sharp row this time; MARS_BAND_TOP is the first clearly-orange row past
 * the blend, MARS_BAND_BOTTOM is the blend's approximate midpoint — same
 * convention as the previous asset. Re-measure both again if this is ever
 * re-exported.
 */
export const MARS_IMAGE_URL = '/mars-worldmap.webp';
/**
 * A 2x-linear (4x-pixel) copy of the same art with finer sand/rock/ice detail, loaded AFTER the 2K one is
 * on screen and swapped in. It covers the same ground, so every constant below stays in 2K "logical" pixels;
 * drawing scales its source rectangle by `marsScale(image)`. Decoded it is ~28 MB (the 2K is ~7 MB), which is
 * why it is skipped on constrained devices — see `shouldLoadHiResMap`.
 */
export const MARS_IMAGE_URL_HIRES = '/mars-worldmap-4k.webp';
export const MARS_IMAGE_WIDTH = 2048;
export const MARS_IMAGE_HEIGHT = 869;
export const MARS_BAND_TOP = 38;
export const MARS_BAND_BOTTOM = 811;

/** How many real pixels per logical (2048-wide) pixel the loaded image has: 1 for the 2K, 2 for the 4K. */
export function marsScale(naturalWidth: number): number {
  return naturalWidth > 0 ? naturalWidth / MARS_IMAGE_WIDTH : 1;
}

export interface MarsConnectionHints {
  saveData?: boolean;
  effectiveType?: string;
}

/**
 * Whether to fetch (and decode) the detailed map at all. The 4K image costs ~28 MB of decoded bitmap on top
 * of the 2K's ~7 MB while both are alive, and several times that once a canvas and GPU texture hold copies —
 * too much for a low-memory phone, and a waste on a metered or very slow link. Everything else gets it.
 */
export function shouldLoadHiResMap(deviceMemoryGb?: number, connection?: MarsConnectionHints): boolean {
  if (connection?.saveData) return false;
  if (connection?.effectiveType && /(^|-)2g$/.test(connection.effectiveType)) return false;
  if (typeof deviceMemoryGb === 'number' && deviceMemoryGb < 4) return false;
  return true;
}

export interface ImagePixel {
  x: number;
  y: number;
}

/**
 * Continuous grid position -> source-image pixel, via the same linear band
 * mapping used throughout (full image width spans the grid's columns; only
 * the orange band spans the grid's rows). No cell-center offset is applied
 * here — callers pass whatever grid position they mean (a cell center via
 * `+0.5`, a cell edge via an integer, or an arbitrary continuous position
 * from inverting the trapezoid projection for the minimap indicator).
 */
export function gridPositionToImagePixel(
  gx: number,
  gy: number,
  imgWidth: number = MARS_IMAGE_WIDTH,
  bandTop: number = MARS_BAND_TOP,
  bandBottom: number = MARS_BAND_BOTTOM,
  gridWidth: number = GRID_WIDTH,
  gridHeight: number = GRID_HEIGHT,
): ImagePixel {
  return {
    x: (gx / gridWidth) * imgWidth,
    y: bandTop + (gy / gridHeight) * (bandBottom - bandTop),
  };
}

export interface ImageRowSlice {
  v0: number;
  v1: number;
}

/**
 * The source-image vertical pixel range [v0, v1) that corresponds to grid
 * row `gy` — used to slice the background image into per-row strips for the
 * trapezoid renderer (see worldmap-screen.ts's drawBackgroundStrips). Each
 * strip is drawn as a single `drawImage` call stretched to that row's
 * trapezoid-narrowed width, so ~40 draws approximate the tilted surface
 * instead of warping pixels individually.
 */
export function imageRowSlice(
  gy: number,
  bandTop: number = MARS_BAND_TOP,
  bandBottom: number = MARS_BAND_BOTTOM,
  gridHeight: number = GRID_HEIGHT,
): ImageRowSlice {
  const rowHeight = (bandBottom - bandTop) / gridHeight;
  const v0 = bandTop + gy * rowHeight;
  return { v0, v1: v0 + rowHeight };
}
